/**
 * FIRST-RUN GUIDES — pure state, no DOM. One small checklist panel per role (free
 * trial, new subscriber, seller, buyer), all built on this and on
 * `ui/first-run-guide.tsx`.
 *
 * A step is done when the product's own data says so (`done` from a real read: the
 * firm has a room, the room has documents…) or when the person did it — a click into
 * the feature, or an action the product noted (`noteGuideActivity`: they opened
 * Manage document, ran a search, asked the room). Both are kept per browser in
 * localStorage, wrapped in try/catch: a private window or blocked storage simply
 * starts fresh and the guide still works for the visit.
 */

/** What a guide remembers: hidden, and the step ids done by a click. */
export interface GuideProgress {
  dismissed: boolean;
  done: string[];
}

export const EMPTY_GUIDE_PROGRESS: GuideProgress = { dismissed: false, done: [] };

/** `closingvault.guide.<guide>[.<scope>].v1` — a scope (a room id) gives each room its own guide. */
export function guideStorageKey(guide: string, scope?: string): string {
  return `closingvault.guide.${guide}${scope ? `.${scope}` : ''}.v1`;
}

/** The browser's localStorage, or null where it is missing or throws on access. */
export function guideStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Read saved progress; anything missing, unreadable or malformed → fresh. */
export function loadGuideProgress(
  storage: Pick<Storage, 'getItem'> | null,
  key: string,
): GuideProgress {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return EMPTY_GUIDE_PROGRESS;
    const parsed = JSON.parse(raw) as { dismissed?: unknown; done?: unknown };
    return {
      dismissed: parsed.dismissed === true,
      done: Array.isArray(parsed.done)
        ? parsed.done.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return EMPTY_GUIDE_PROGRESS;
  }
}

/** Persist progress; a storage failure is ignored (the guide still works this visit). */
export function saveGuideProgress(
  storage: Pick<Storage, 'setItem'> | null,
  key: string,
  progress: GuideProgress,
): void {
  try {
    storage?.setItem(key, JSON.stringify(progress));
  } catch {
    // Blocked storage: progress lives for this page only.
  }
}

/** A step as the guide shows it: a done flag from real data, or sub-steps (all must be done). */
export interface GuideStepInput {
  id: string;
  /** True when the product's own data says the step is done. */
  done?: boolean;
  parts?: { id: string; done?: boolean }[];
}

export interface GuideStepState {
  id: string;
  done: boolean;
  parts: { id: string; done: boolean }[];
}

/** Mark each step (and sub-step) done: from real data, or from what the person did. */
export function resolveGuideSteps(
  steps: readonly GuideStepInput[],
  progress: GuideProgress,
  activity: ReadonlySet<string> = new Set(),
): GuideStepState[] {
  const did = (id: string, derived?: boolean) =>
    derived === true || progress.done.includes(id) || activity.has(id);
  return steps.map((step) => {
    const parts = (step.parts ?? []).map((p) => ({ id: p.id, done: did(p.id, p.done) }));
    return {
      id: step.id,
      parts,
      done: parts.length > 0 ? parts.every((p) => p.done) : did(step.id, step.done),
    };
  });
}

/** The first step not yet done, or null when the guide is complete. */
export function currentGuideStep(steps: readonly GuideStepState[]): string | null {
  return steps.find((s) => !s.done)?.id ?? null;
}

/** `show` the guide, offer to `resume` a hidden unfinished one, or `none` (finished). */
export function guideMode(
  steps: readonly GuideStepState[],
  progress: GuideProgress,
): 'show' | 'resume' | 'none' {
  if (currentGuideStep(steps) === null) return 'none';
  return progress.dismissed ? 'resume' : 'show';
}

// --- What the person did elsewhere in the product ------------------------------------

/**
 * Things a person does in the product that a guide step can tick from. A step (or
 * sub-step) whose id is one of these ticks when it is noted.
 */
export type GuideActivity = 'manage-document' | 'search' | 'ask' | 'folder' | 'processing';

const ACTIVITY_KEY = 'closingvault.guide-activity.v1';

/** Tells every mounted guide something changed (progress or activity). */
export const GUIDE_CHANGE_EVENT = 'closingvault:guide-change';

export function emitGuideChange(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(GUIDE_CHANGE_EVENT));
  } catch {
    // No DOM (server render, tests in node): nothing is listening.
  }
}

/** The activity noted so far in this browser. */
export function loadGuideActivity(storage: Pick<Storage, 'getItem'> | null): Set<string> {
  try {
    const raw = storage?.getItem(ACTIVITY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

// With storage blocked, activity is remembered for this page only.
let pageActivity = new Set<string>();

/** The activity noted in this browser. */
export function currentGuideActivity(): Set<string> {
  const storage = guideStorage();
  return storage ? loadGuideActivity(storage) : new Set(pageActivity);
}

/** Note that the person did something a guide may be waiting for. Cheap and safe to call anywhere. */
export function noteGuideActivity(activity: GuideActivity): void {
  const have = currentGuideActivity();
  if (have.has(activity)) return;
  have.add(activity);
  pageActivity = have;
  try {
    guideStorage()?.setItem(ACTIVITY_KEY, JSON.stringify([...have]));
  } catch {
    // Blocked storage: remembered for this page only.
  }
  emitGuideChange();
}

/** Test hook: forget the page-lifetime activity. */
export function resetGuideActivityForTests(): void {
  pageActivity = new Set();
}
